export async function authRequest(action, body = {}) {
  const response = await fetch(`/api/auth/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.message || 'Something went wrong. Please try again.');
    error.status = response.status;
    // Only ever set on a 429 from the OTP endpoints (IN-332). The seconds
    // come from the API's own `Retry-After`, so a screen that holds a button
    // for this long is honouring the upstream limit rather than inventing a
    // second one beside it.
    if (payload.retry_after) error.retryAfter = Number(payload.retry_after);
    throw error;
  }
  return payload;
}

/**
 * Constrain a `?next=` value to a path on this origin.
 *
 * Browsers normalise backslashes to forward slashes in URLs, so `/\evil.com`
 * would navigate off-site as `//evil.com` — backslashes are rejected outright
 * rather than only checking for a leading `//`.
 */
export function safeNext(value) {
  if (typeof value !== 'string') return '/';
  if (!value.startsWith('/')) return '/';
  if (value.startsWith('//')) return '/';
  if (value.includes('\\')) return '/';
  if (/[\x00-\x1f\x7f]/.test(value)) return '/';
  return value;
}

/**
 * Clear the session and go to sign-in. The only way out of the app.
 *
 * There were three copies of this and they behaved differently, which is how
 * people got stranded. The shape matters in two ways that are easy to get
 * wrong:
 *
 * **The cookies are cleared before navigating, and the call is awaited.**
 * `proxy.js` decides "signed in" from the session cookie's *presence*,
 * never its validity, so navigating with the cookie still set means
 * `/auth/sign-in` is treated as a signed-out-only page and bounces straight
 * to `/dashboard`. Firing the logout and navigating in the same tick races
 * the `Set-Cookie` and loses about half the time.
 *
 * **It is a full navigation, not `router.push`.** The cookies just changed
 * and every server component needs to resolve against the new state; a
 * client-side transition can reuse a cached render of a signed-in shell.
 *
 * `signed-out=1` is the belt to that braces: `proxy.js` honours it and
 * lets sign-in render even if the cookies somehow survived, so a failed
 * logout degrades to "sign in again" rather than to a redirect loop with no
 * way out but clearing site data by hand.
 */
export async function signOut({ next } = {}) {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
  } catch {
    // Offline, or the route is unreachable. Go anyway — `signed-out=1` keeps
    // sign-in reachable, and staying put strands the user on a dead session.
  }
  const target = next ? `&next=${encodeURIComponent(safeNext(next))}` : '';
  window.location.assign(`/auth/sign-in?signed-out=1${target}`);
}
